import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ACCESS_ERROR_MESSAGES } from '../error/accessErrorMessages';
import type { AccessErrorRouteState } from '../error/accessErrorNavigation';
import { ErrorPageShell } from '../error/ErrorPageShell';
import { resolveErrorPageVariant } from '../error/errorPageVariant.util';
import { ErrorStateCard } from '../error/ErrorStateCard';
import {
  buildBaseErrorLogPayload,
  ensureUpstreamErrorLogDelivered,
  logError,
  resolveUpstreamErrorLogPayload,
} from '../services/errorLogger';
import { contactUsEmail } from '../util/constants';
import { goToParentProductHomeOrCategoryHome } from '../util/parentShell';

function AccessError() {
  const location = useLocation();
  const state = (location.state as AccessErrorRouteState | null) ?? {};
  // Always the standard "Access Required" copy — the API's own message (state.detail)
  // is intentionally not shown to the user.
  const variant = resolveErrorPageVariant(403);

  // When the 403 came from an API call, the axios interceptor already logged it
  // with the request's X-Request-ID and passes that correlationId here — reuse it
  // (same Ref. on screen, in Copy and in the email as in /logs) and don't log again.
  // Only a visit with no upstream correlationId builds and logs its own.
  const upstreamCorrelationId =
    typeof state.correlationId === 'string' && state.correlationId ? state.correlationId : undefined;

  const errorPayload = useMemo(
    () =>
      upstreamCorrelationId
        ? resolveUpstreamErrorLogPayload(upstreamCorrelationId, ACCESS_ERROR_MESSAGES.title)
        : buildBaseErrorLogPayload('ui-action', 'warning', ACCESS_ERROR_MESSAGES.title),
    [upstreamCorrelationId]
  );

  useEffect(() => {
    if (upstreamCorrelationId) {
      ensureUpstreamErrorLogDelivered(upstreamCorrelationId);
      return;
    }
    void logError(errorPayload);
  }, [errorPayload, upstreamCorrelationId]);

  return (
    <ErrorPageShell secondaryText={variant.secondaryText} secondaryActionLabel={variant.secondaryActionLabel}>
      <ErrorStateCard
        icon={variant.icon}
        badgeIcon={variant.badgeIcon}
        illustration={variant.illustration}
        title={ACCESS_ERROR_MESSAGES.title}
        correlationId={errorPayload.correlationId}
        logPayload={errorPayload}
        goHomeLabel={ACCESS_ERROR_MESSAGES.ctaLabel}
        onGoHome={() => goToParentProductHomeOrCategoryHome()}
        description={
          <p>
            {ACCESS_ERROR_MESSAGES.descriptionBeforeSupport}
            <a href={`mailto:${contactUsEmail}`}>
              {ACCESS_ERROR_MESSAGES.supportLinkLabel}
            </a>
            {ACCESS_ERROR_MESSAGES.descriptionAfterSupport}
          </p>
        }
      />
    </ErrorPageShell>
  );
}

export default AccessError;
